import React from 'react';
import { useSWRConfig } from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import BulkImportStatus from '../config/BulkImportStatus';
import type BulkImport from '../types/BulkImport';
import useUpdateBulkImport from './useUpdateBulkImport';

const useCancelUploadDialog = () => {
  const ownerOrgId = useOwnerOrgId();
  const { mutate, cache: swrCache } = useSWRConfig();
  const { openDialog, closeDialog } = useDialogManager();
  const { user } = useCurrentUser();
  const updateBulkImport = useUpdateBulkImport();

  const deleteBulkImportFromCache = React.useCallback(
    (bulkImportId: string) => {
      const key = `bulk-imports?${joinNonEmptyParams({ ownerOrgId })}`;

      if (!swrCache.get(key)) return;

      mutate<BulkImport[]>(
        key,
        (currentData) => {
          if (currentData) {
            const idx = currentData.findIndex(
              (item) => item.bulkImportId === bulkImportId,
            );
            if (idx !== -1) {
              const newData = [...currentData];
              newData.splice(idx, 1);
              return newData;
            }
          }
          return currentData;
        },
        false,
      );
    },
    [mutate, ownerOrgId, swrCache],
  );

  const showCancelUploadDialog = React.useCallback(
    (bulkImportId: string) => {
      openDialog(Dialogs.CONFIRMATION, {
        children: translate('DATA_UPLOADS_CANCEL_DATA_UPLOAD_MESSAGE'),
        confirmLabel: translate('CONFIRM'),
        headerText: (
          <span className="capitalize">
            {translate('DATA_UPLOADS_CANCEL_DATA_UPLOAD')}
          </span>
        ),
        onCancel: closeDialog,
        onClose: closeDialog,
        onConfirm: async () => {
          try {
            await updateBulkImport({
              bulkImportId,
              status: BulkImportStatus.CANCELED,
            });

            deleteBulkImportFromCache(bulkImportId);
          } catch (e) {
            //
          }
        },
      });
    },
    [deleteBulkImportFromCache, ownerOrgId, user],
  );

  return showCancelUploadDialog;
};

export default useCancelUploadDialog;
